import type React from 'react'
import { Link as RouterLink } from 'react-router-dom'

/**
 * Returns the current base path prefix for the app.
 */
export const getBasePath = (): string => {
  const { pathname } = window.location

  return pathname === '/react' || pathname.startsWith('/react/') ? '/react' : ''
}

export const isExternalUrl = (url: string): boolean => {
  return url.startsWith('http')
}

export const isAppPath = (url: string): boolean => url.startsWith('/') && !url.startsWith('//')

export const ensureUrlProtocol = (url: string): string => {
  const schemeMatcher = /^([a-z][a-z\d+-]*:|[/#?.]|\{\{)/i
  const trimmedUrl = url.trim()
  return schemeMatcher.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`
}

export const getSafeRedirectPath = (url: string | null | undefined): string | null => {
  if (!url || /[\u0000-\u001f\\]/.test(url)) return null
  let parsed: URL
  try {
    parsed = new URL(url, window.location.origin)
  } catch {
    return null
  }
  if (parsed.origin !== window.location.origin) return null
  const isReactPath = parsed.pathname === '/react' || parsed.pathname.startsWith('/react/')
  const pathname = isReactPath ? parsed.pathname.slice('/react'.length) || '/' : parsed.pathname
  return `${pathname}${parsed.search}${parsed.hash}`
}

export const toAbsoluteUrl = (url: string): string => {
  if (!isAppPath(url)) return url

  return `${window.location.origin}${getBasePath()}${url}`
}

export const getLinkProps = (
  to?: string,
  newTab = false,
): {
  component?: 'a' | typeof RouterLink
  href?: string
  to?: string
  target?: string
  rel?: string
} => {
  if (!to) return {}
  if (newTab) {
    return {
      component: 'a',
      href: toAbsoluteUrl(to),
      target: '_blank',
      rel: 'noopener noreferrer',
    }
  }

  return isAppPath(to) ? { component: RouterLink, to } : { component: 'a', href: to }
}

export const openInNewTab = (url: string): void => {
  window.open(toAbsoluteUrl(url), '_blank', 'noopener,noreferrer')
}

export const reserveNewTab = (): { navigate: (url: string) => void; close: () => void } => {
  const reservedTab = window.open('', '_blank')
  if (reservedTab) reservedTab.opener = null

  return {
    navigate: (url: string): void => {
      if (reservedTab) {
        if (!reservedTab.closed) reservedTab.location.href = toAbsoluteUrl(url)
      } else {
        window.location.href = toAbsoluteUrl(url)
      }
    },
    close: () => reservedTab?.close(),
  }
}

export const navigateWithFallback = (
  url: string,
  navigate: (path: string) => void,
  newTab = false,
): void => {
  if (isExternalUrl(url)) {
    window.open(url, newTab ? '_blank' : '_self', 'noopener,noreferrer')
  } else {
    if (newTab) {
      window.open(`${window.location.origin}${getBasePath()}${url}`, '_blank')
    } else {
      navigate(url)
    }
  }
}

export const handleNavigationClick = (
  url: string,
  navigate: (path: string) => void,
  event?: React.MouseEvent<Element, MouseEvent>,
): void => {
  const shouldOpenInNewTab = event && (event.ctrlKey || event.metaKey)

  if (isExternalUrl(url)) {
    window.open(url, shouldOpenInNewTab ? '_blank' : '_self', 'noopener,noreferrer')
  } else {
    if (shouldOpenInNewTab) {
      window.open(`${window.location.origin}${getBasePath()}${url}`, '_blank')
    } else {
      navigate(url)
    }
  }
}

export const convertFullUrlToPath = (fullUrl: string): string => {
  if (isExternalUrl(fullUrl)) {
    return fullUrl
  }

  return fullUrl.startsWith('/') ? fullUrl : `/${fullUrl}`
}
